import { t } from "i18next";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "../../../../../hooks/useFetch";
import { FShift, Shift } from "../../../../../types/types";
import { Label } from "../../../../../ui/Label/Label";
import { SelectButton } from "../../../../../ui/SelectButton/SelectButton";
import { times } from "../../../../../enums/times";
import { DateTime } from "luxon";
import { ScrollArea } from "../../../../../ui/ScrollArea/ScrollArea";
import { Separator } from "../../../../../ui/Separator";
import { Button } from "../../../../../ui/Button";
import DeleteShift from "../../DeleteShift";
import { Input } from "../../../../../ui/Input/Input";
import { useAppStore } from "../../../../../stores/RootStore";

type Props = {
  id: string | undefined;
  currentShift: FShift | undefined;
};

const ShiftSettingsTab = ({ id, currentShift }: Props) => {
  const { currentUser, currentSite } = useAppStore();
  const isRestricted = !currentUser?.attributes.admin && currentSite?.user_status == "user";
  //const [errors, setErrors] = useState([]);
  const start = currentShift?.start_at.toLocaleString(DateTime.TIME_24_SIMPLE)!;
  const end = currentShift?.end_at.toLocaleString(DateTime.TIME_24_SIMPLE)!;

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { handleSubmit, reset, control } = useForm({
    defaultValues: {
      shift_name: currentShift?.name,
      shift_start_time: { value: start, label: start },
      shift_end_time: { value: end, label: end },
    },
  });

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  // Обновление кэша (значения по умолчанию) при изменении смены в компактной версии
  useEffect(() => {
    reset({
      shift_name: currentShift?.name,
      shift_start_time: { value: start, label: start },
      shift_end_time: { value: end, label: end },
    });
  }, [id]);

  // Обновление информации о смене
  const [{ response: itemSaveResponse, isLoading: itemSaveIsLoading, error: itemSaveError }, doFetchItemSave] =
    useFetch(`/directories/working_shifts/${id}`);

  const onSubmit = (formData: any) => {
    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          name: formData.shift_name,
          started_at: formData.shift_start_time.value,
          ended_at: formData.shift_end_time.value,
        },
      },
    });
  };

  const shifts = useAppStore((store) => store.shifts);
  const setShifts = useAppStore((store) => store.setShifts);

  const editShift = (itemSaveResponse: { data: unknown }) => {
    const newShiftData = itemSaveResponse.data as unknown as Shift;

    // Пересборка того что приходит с бэка
    let start = DateTime.fromISO(newShiftData.attributes.started_at)
    let end = DateTime.fromISO(newShiftData.attributes.ended_at)
    let build_start = DateTime.fromObject({year: 2000, month: 1, day: 1, hour: start.hour, minute: start.minute, second: start.second})
    let build_end = DateTime.fromObject({year: 2000, month: 1, day: 1, hour: end.hour, minute: end.minute, second: end.second})

    const ShiftData = {
      id: newShiftData.id,
      name: newShiftData.attributes.name,
      start_at: build_start,
      end_at: build_end,
    } as unknown as FShift;
    const withoutCurrentItem = shifts?.filter((shift) => shift.id != currentShift?.id)!;
    setShifts([...withoutCurrentItem, ShiftData]);
  };

  useEffect(() => {
    if (!itemSaveResponse) return;
    editShift(itemSaveResponse);
  }, [itemSaveResponse]);

  return (
    <>
      <div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ScrollArea>
            <div className="flex flex-col gap-3">
              <Controller
                name="shift_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("shift_name")}`} />
                  </div>
                )}
              />
              <Controller
                name="shift_start_time"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("shift_start_time")}*</Label>
                    <SelectButton
                      disabled={isRestricted}
                      label={"Выберите время"}
                      searchable={true}
                      options={times}
                      value={value ? times.find((s) => s.value == String(value.value))! : null}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
              <Controller
                name="shift_end_time"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("shift_end_time")}*</Label>
                    <SelectButton
                      disabled={isRestricted}
                      label={"Выберите время"}
                      searchable={true}
                      options={times}
                      value={value ? times.find((s) => s.value == String(value.value))! : null}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
              <div className="mt-8 flex w-full justify-between">
                <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                  {t("delete")}
                </Button>
                <Button variant={"default"} size={"sm"} type="submit">
                  {t("save")}
                </Button>
              </div>
            </div>
          </ScrollArea>
        </form>
      </div>
      <DeleteShift id={id!} isDeleteOpen={isDeleteOpen} closeModal={closeDeleteModal} />
    </>
  );
};

export default ShiftSettingsTab;
